/**
 * Frame mode vs device mode for the app page.
 *
 * - Frame (desktop, tests): the app renders inside the fake iPhone bezel with
 *   a drawn status bar and the toolbar's theme / Text size controls.
 * - Device (a real phone): the app fills the viewport, the OS draws the status
 *   bar, and the layout engine gets the real safe-area insets from
 *   `env(safe-area-inset-*)`, exposed as `--sb-inset-*` on `:root`.
 *
 * `?mode=device` / `?mode=frame` force a mode; otherwise a coarse pointer
 * means a touch device at any width (Phase 7: a tablet fills the viewport
 * instead of showing the phone bezel; the old 500 px limit is gone), and a
 * site built for a deployment (`__SB_PAGE_MODE__` of `device`, see
 * plugins/site-layout.ts) is device mode on every screen: a desktop browser
 * gets the app's regular-width (iPad) layout, not a phone in a bezel. The
 * inline script in app/index.html applies the same rule before the first
 * paint.
 */

import type { EdgeInsets } from './layout';

export type PageMode = 'frame' | 'device';

/** The site's default for a fine pointer: `auto` is the frame, `device` the full window. */
export type DefaultPageMode = 'auto' | 'device';

export function decidePageMode(
  search: string = location.search,
  win: Pick<Window, 'matchMedia'> = window,
  defaultMode: DefaultPageMode = 'auto',
): PageMode {
  const mode = new URLSearchParams(search).get('mode');
  if (mode === 'device') return 'device';
  if (mode === 'frame') return 'frame';
  const coarse = typeof win.matchMedia === 'function' && win.matchMedia('(pointer: coarse)').matches;
  return coarse || defaultMode === 'device' ? 'device' : 'frame';
}

/** Mark the document so styles.css can switch the chrome. */
export function applyPageMode(mode: PageMode): void {
  document.documentElement.dataset['sbMode'] = mode;
}

/**
 * The real safe-area insets, read from the `--sb-inset-*` custom properties
 * (`env(safe-area-inset-*)`, 0 outside standalone / landscape). Values that
 * do not parse count as 0.
 */
export function readViewportInsets(): EdgeInsets {
  const cs = getComputedStyle(document.documentElement);
  const read = (name: string): number => {
    const value = parseFloat(cs.getPropertyValue(name));
    return Number.isFinite(value) && value > 0 ? value : 0;
  };
  return {
    top: read('--sb-inset-top'),
    leading: read('--sb-inset-left'),
    bottom: read('--sb-inset-bottom'),
    trailing: read('--sb-inset-right'),
  };
}

/**
 * Call `onChange` (debounced) whenever the viewport may have changed size or
 * insets: window `resize`, `orientationchange` and `visualViewport` resizes.
 * Returns a function that stops listening.
 */
export function watchViewport(onChange: () => void, debounceMs = 100): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const schedule = (): void => {
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = undefined;
      onChange();
    }, debounceMs);
  };
  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  const vv = window.visualViewport;
  vv?.addEventListener('resize', schedule);
  return () => {
    if (timer !== undefined) clearTimeout(timer);
    window.removeEventListener('resize', schedule);
    window.removeEventListener('orientationchange', schedule);
    vv?.removeEventListener('resize', schedule);
  };
}

/** What `keyboardHeight` reads of the visual viewport (`window.visualViewport`). */
export interface VisualViewportLike {
  height: number;
  scale: number;
}

/**
 * The height of the on-screen keyboard over the bottom of the layout
 * viewport, in CSS px: 0 while it is down. `layoutHeight` is the layout
 * viewport's height: the height of the root element that fills it
 * (`100dvh`), not `window.innerHeight`, which iOS Safari shrinks to the
 * visual viewport's height while the keyboard is up (an iPhone read inner
 * 352, visual 352, and the root still 692 tall). iOS Safari (and a browser
 * whose `interactive-widget` is `resizes-visual`) does not shrink the layout
 * viewport for the keyboard; it shrinks the visual viewport to the part of
 * the screen above the keyboard and pans it down the page so the focused
 * field shows. The keyboard is therefore the difference of the two heights.
 * The pan (`offsetTop`) is not subtracted: Safari pans by the keyboard's
 * full height when the field is at the bottom of the screen, which would
 * read as no keyboard at all; `watchKeyboard` undoes the pan instead. A
 * browser that shrinks the layout viewport for the keyboard measures 0 here
 * and resizes the root. A pinch zoom shrinks the visual viewport too, so a
 * scale other than 1 counts as no keyboard.
 */
export function keyboardHeight(layoutHeight: number, vv: VisualViewportLike | null | undefined): number {
  if (!vv || !Number.isFinite(vv.height) || !Number.isFinite(layoutHeight) || Math.abs((vv.scale || 1) - 1) > 0.01) return 0;
  const covered = layoutHeight - vv.height;
  return covered > 0 ? Math.round(covered) : 0;
}

export interface WatchKeyboardOptions {
  /** The element that fills the layout viewport (`#device`): its height is the layout viewport's. */
  root: HTMLElement;
  /** Gets every reading (the `?debug=viewport` readout). */
  onRead?: ((reading: ViewportReading) => void) | undefined;
  /** Where the last measured height is kept for the next focus (and the next page load); `localStorage` by default, null for none. */
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null | undefined;
}

/**
 * Call `onChange` with the keyboard's height (`keyboardHeight`) whenever the
 * visual viewport resizes or pans, once per animation frame: the iOS
 * keyboard animates over a quarter of a second and sends several resizes,
 * so unlike `watchViewport` this does not debounce. A text input gaining or
 * losing focus is what raises or drops the keyboard, so for a second after
 * either the viewport is read every frame as well, in case its events come
 * late or not at all.
 *
 * The browser pans the page to the focused field when the keyboard would
 * cover it, which would leave the top of the screen (the navigation bar)
 * scrolled off, and it does so while the keyboard slides, so undoing the
 * pan as it happens shows as a judder. Better that it never happens: on
 * focus the keyboard's last measured height (kept per layout height, in
 * `localStorage` so the next page load has it too) is reported at once,
 * before the keyboard shows, so the app has laid its content out above the
 * keyboard, field included, by the time the browser decides whether to pan.
 * The measurement then confirms or corrects it; a reading of 0 during the
 * second after the focus is the keyboard not up yet, not a correction. A
 * pan that does happen (the first focus ever, a keyboard taller than the
 * last) is scrolled back, once it has held still for a few frames: Safari
 * animates its pan with the keyboard's slide, and scrolling against that
 * animation showed as a judder. (Safari's pan is a window scroll, `scrollY`
 * 340 for a 340 keyboard on an iPhone; moving the root to counter a pan read
 * from `visualViewport.offsetTop` was tried and dipped the content mid-slide
 * on passing offsets.) When the field loses focus and no other text input
 * has it, 0 is reported at once too, as the keyboard starts down. Returns a
 * function that stops listening.
 */
export function watchKeyboard(onChange: (height: number) => void, options: WatchKeyboardOptions): () => void {
  const vv = window.visualViewport;
  if (!vv) return () => {};
  const { root, onRead } = options;
  const storage = options.storage === undefined ? safeLocalStorage() : options.storage;
  let frame: number | undefined;
  let last = 0;
  /** While set, the viewport is read every frame until this time (performance.now() ms). */
  let pollUntil = 0;
  /** While set, a reading of 0 is the keyboard not up yet (a focus just reported the known height). */
  let anticipatingUntil = 0;
  let blurTimer: ReturnType<typeof setTimeout> | undefined;
  let lastEvent = '';
  /** The pan read last frame (`scrollY:offsetTop`) and for how many frames in a row it has held. */
  let lastPan = '';
  let panHeld = 0;
  const storageKey = (): string => `sb-keyboard-${root.clientHeight}`;
  const knownHeight = (): number => {
    const value = Number(storage?.getItem(storageKey()) ?? 0);
    return Number.isFinite(value) && value > 0 ? value : 0;
  };
  const remember = (height: number): void => {
    try {
      storage?.setItem(storageKey(), String(height));
    } catch {
      // Storage full or blocked: the next focus measures instead of anticipating.
    }
  };
  const report = (height: number): void => {
    if (height === last) return;
    last = height;
    onChange(height);
  };
  const update = (): void => {
    frame = undefined;
    const layoutHeight = root.clientHeight;
    const height = keyboardHeight(layoutHeight, vv);
    const up = height > 0 || (last > 0 && performance.now() < anticipatingUntil);
    const panNow = `${Math.round(window.scrollY)}:${Math.round(vv.offsetTop)}`;
    panHeld = panNow === lastPan ? panHeld + 1 : 0;
    lastPan = panNow;
    const settled = panHeld >= PAN_SETTLE_FRAMES;
    if (up && settled && (window.scrollY !== 0 || window.scrollX !== 0)) window.scrollTo(0, 0);
    onRead?.({
      layoutHeight,
      innerHeight: window.innerHeight,
      visualHeight: vv.height,
      scale: vv.scale,
      offsetTop: vv.offsetTop,
      pageTop: vv.pageTop,
      scrollY: window.scrollY,
      keyboard: height,
      event: lastEvent,
    });
    // The readout wants every frame; otherwise only the second after a focus change.
    if (onRead || performance.now() < pollUntil) schedule();
    if (height > 0) {
      anticipatingUntil = 0;
      remember(height);
      report(height);
    } else if (performance.now() >= anticipatingUntil) {
      report(0);
    }
  };
  const schedule = (): void => {
    if (frame === undefined) frame = requestAnimationFrame(update);
  };
  const onViewport = (event: Event): void => {
    lastEvent = `visualViewport ${event.type}`;
    // Read on for a moment after a change, to see the pan hold still.
    pollUntil = Math.max(pollUntil, performance.now() + PAN_SETTLE_MS);
    schedule();
  };
  const isTextInput = (target: EventTarget | Element | null): boolean =>
    target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;
  const onFocus = (event: FocusEvent): void => {
    if (!isTextInput(event.target)) return;
    lastEvent = event.type;
    pollUntil = performance.now() + FOCUS_POLL_MS;
    if (blurTimer !== undefined) clearTimeout(blurTimer);
    blurTimer = undefined;
    if (event.type === 'focusin') {
      const known = knownHeight();
      if (known > 0) {
        anticipatingUntil = pollUntil;
        report(known);
      }
    } else {
      anticipatingUntil = 0;
      // The focus may be moving to another text input (its `focusin` follows at once): wait a moment.
      blurTimer = setTimeout(() => {
        blurTimer = undefined;
        if (!isTextInput(document.activeElement)) report(0);
      }, BLUR_SETTLE_MS);
    }
    schedule();
  };
  vv.addEventListener('resize', onViewport);
  vv.addEventListener('scroll', onViewport);
  document.addEventListener('focusin', onFocus);
  document.addEventListener('focusout', onFocus);
  return () => {
    if (frame !== undefined) cancelAnimationFrame(frame);
    if (blurTimer !== undefined) clearTimeout(blurTimer);
    pollUntil = 0;
    anticipatingUntil = 0;
    vv.removeEventListener('resize', onViewport);
    vv.removeEventListener('scroll', onViewport);
    document.removeEventListener('focusin', onFocus);
    document.removeEventListener('focusout', onFocus);
  };
}

/** `localStorage`, or null where reading it throws (a private window, blocked site data). */
function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** After a text input loses focus, how long to wait for another to take it before reporting the keyboard down. */
const BLUR_SETTLE_MS = 60;

/** How many frames in a row the browser's pan must hold still before it is scrolled back. */
const PAN_SETTLE_FRAMES = 3;

/** How long after a visual viewport event the viewport keeps being read every frame, to see the pan settle. */
const PAN_SETTLE_MS = 250;

/** How long after a text input gains or loses focus the viewport is read every frame (the keyboard's animation and some). */
const FOCUS_POLL_MS = 1000;

/** One reading of the viewport, for the `?debug=viewport` readout. */
export interface ViewportReading {
  /** The root's height: the layout viewport's. */
  layoutHeight: number;
  innerHeight: number;
  visualHeight: number;
  scale: number;
  /** The visual viewport's offset within the layout viewport: the browser's pan to the focused field. */
  offsetTop: number;
  pageTop: number;
  scrollY: number;
  keyboard: number;
  /** The event that led to this reading (`visualViewport resize`, `focusin`…), empty before the first. */
  event: string;
}

/**
 * `?debug=viewport`: a small readout over the app with the numbers the
 * keyboard measurement reads, for checking a phone's browser (which has no
 * console to hand). It follows the visual viewport (a fixed element is
 * panned with the layout viewport, off the top of the screen, when the
 * browser pans to a field). Returns the function that updates it.
 */
export function installViewportReadout(): (reading: ViewportReading) => void {
  const el = document.createElement('pre');
  el.className = 'sb-viewport-readout';
  el.setAttribute('aria-hidden', 'true');
  document.body.appendChild(el);
  return (r) => {
    el.style.transform = `translateY(${Math.round(r.offsetTop)}px)`;
    el.textContent =
      `root ${window.innerWidth}×${r.layoutHeight}  inner ${r.innerHeight}  visual ${Math.round(r.visualHeight)}  scale ${r.scale.toFixed(3)}\n` +
      `offsetTop ${Math.round(r.offsetTop)}  pageTop ${Math.round(r.pageTop)}  scrollY ${Math.round(r.scrollY)}\n` +
      `keyboard ${r.keyboard}  ${r.event || 'no event yet'}  ${new Date().toLocaleTimeString()}`;
  };
}
